import type { ComponentPropsWithRef, ReactNode } from 'react';

import { cn } from '@/lib/cn';
import { Spinner } from './Spinner';

const VARIANTS = {
  primary: 'bg-brand text-brand-ink hover:bg-brand-strong active:bg-brand-strong',
  secondary:
    'border border-line bg-surface-raised text-ink hover:bg-surface-overlay active:bg-surface-overlay',
  ghost: 'text-ink-muted hover:bg-surface-raised hover:text-ink',
  danger: 'bg-danger text-brand-ink hover:opacity-90 active:opacity-90',
} as const;

const SIZES = {
  // 44px es el objetivo táctil mínimo recomendado.
  md: 'min-h-11 px-4 text-sm',
  lg: 'min-h-13 px-5 text-base',
} as const;

export interface ButtonProps extends ComponentPropsWithRef<'button'> {
  variant?: keyof typeof VARIANTS;
  size?: keyof typeof SIZES;
  /** Muestra un spinner y desactiva el botón. */
  loading?: boolean;
  block?: boolean;
  icon?: ReactNode;
}

/** Botón táctil (≥ 44 px de alto) con estado de carga integrado. */
export function Button({
  variant = 'primary',
  size = 'md',
  loading = false,
  block = false,
  icon,
  className,
  children,
  disabled,
  type = 'button',
  ...props
}: ButtonProps) {
  return (
    <button
      type={type}
      disabled={disabled === true || loading}
      aria-busy={loading || undefined}
      className={cn(
        'inline-flex select-none items-center justify-center gap-2 rounded-xl font-semibold',
        'transition-colors disabled:cursor-not-allowed disabled:opacity-50',
        VARIANTS[variant],
        SIZES[size],
        block && 'w-full',
        className,
      )}
      {...props}
    >
      {loading ? <Spinner size="sm" label="Enviando" /> : icon}
      {children}
    </button>
  );
}
